import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * SmartDateInput
 * --------------
 * Hybrid date input optimised for kitchen workflows where expiration dates can
 * be 1-3 years in the future.
 *
 * Features:
 *  - Free typing: accepts `YYYY-MM-DD` and `DD/MM/YYYY`.
 *  - Calendar popover with month + year dropdown navigation.
 *  - Returns ISO `YYYY-MM-DD` via `onChange(isoString)` (or "" when cleared).
 *  - `inputMode="numeric"` so mobile shows the digits keypad.
 *
 * Props:
 *  - value: ISO date string (YYYY-MM-DD) or ""
 *  - onChange: (isoString) => void
 *  - placeholder, disabled, ...
 */

const isoRe = /^(\d{4})-(\d{2})-(\d{2})$/;
const euRe = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

function parseAny(text) {
  if (!text) return null;
  const t = text.trim();
  let m = t.match(isoRe);
  if (m) {
    const y = +m[1], mo = +m[2], d = +m[3];
    const dt = new Date(Date.UTC(y, mo - 1, d));
    if (dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d) return dt;
  }
  m = t.match(euRe);
  if (m) {
    const d = +m[1], mo = +m[2], y = +m[3];
    const dt = new Date(Date.UTC(y, mo - 1, d));
    if (dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d) return dt;
  }
  return null;
}

function toISO(date) {
  if (!date) return "";
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function fromISO(iso) {
  if (!iso || !isoRe.test(iso)) return null;
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  return new Date(Date.UTC(y, m - 1, d));
}

const MONTHS = [
  { v: 0, label: "Ene" }, { v: 1, label: "Feb" }, { v: 2, label: "Mar" },
  { v: 3, label: "Abr" }, { v: 4, label: "May" }, { v: 5, label: "Jun" },
  { v: 6, label: "Jul" }, { v: 7, label: "Ago" }, { v: 8, label: "Sep" },
  { v: 9, label: "Oct" }, { v: 10, label: "Nov" }, { v: 11, label: "Dic" },
];

export default function SmartDateInput({
  value = "",
  onChange,
  placeholder = "dd/mm/aaaa o 2027-05-19",
  disabled = false,
  "data-testid": testId,
  yearRangeBack = 1,
  yearRangeForward = 10,
}) {
  // Local text state lets users type freely; we only commit when valid.
  const [text, setText] = useState("");
  const [invalid, setInvalid] = useState(false);
  const [open, setOpen] = useState(false);
  const [calMonth, setCalMonth] = useState(() => fromISO(value) || new Date());

  // Sync local text from controlled value
  useEffect(() => {
    if (!value) { setText(""); setInvalid(false); return; }
    const d = fromISO(value);
    if (d) {
      const dd = String(d.getUTCDate()).padStart(2, "0");
      const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
      setText(`${dd}/${mm}/${d.getUTCFullYear()}`);
      setCalMonth(d);
      setInvalid(false);
    }
  }, [value]);

  const commit = (raw) => {
    setText(raw);
    if (!raw.trim()) { setInvalid(false); onChange?.(""); return; }
    const parsed = parseAny(raw);
    if (parsed) {
      setInvalid(false);
      onChange?.(toISO(parsed));
      setCalMonth(parsed);
    } else {
      // Partial / invalid: don't commit, but flag if it already has digits/separators
      setInvalid(/[/-]/.test(raw) && raw.length >= 8);
    }
  };

  const onCalendarSelect = (d) => {
    if (!d) return;
    onChange?.(toISO(d));
    setOpen(false);
  };

  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear - yearRangeBack; y <= currentYear + yearRangeForward; y++) years.push(y);

  return (
    <div className="flex gap-2">
      <Input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder}
        value={text}
        onChange={(e) => commit(e.target.value)}
        disabled={disabled}
        className={cn("flex-1", invalid && "border-red-400 text-red-600 focus-visible:ring-red-300")}
        data-testid={testId}
        aria-invalid={invalid}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon"
            disabled={disabled}
            className={cn("flex-shrink-0", !value && "text-muted-foreground")}
            data-testid={testId ? `${testId}-picker-btn` : undefined}
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          {/* Month + Year fast nav */}
          <div className="flex gap-2 p-3 border-b">
            <Select
              value={String(calMonth.getMonth())}
              onValueChange={(v) => {
                const nm = new Date(calMonth);
                nm.setMonth(parseInt(v, 10));
                setCalMonth(nm);
              }}
            >
              <SelectTrigger className="h-8 text-xs flex-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((m) => (
                  <SelectItem key={m.v} value={String(m.v)} className="text-xs">{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={String(calMonth.getFullYear())}
              onValueChange={(v) => {
                const nm = new Date(calMonth);
                nm.setFullYear(parseInt(v, 10));
                setCalMonth(nm);
              }}
            >
              <SelectTrigger className="h-8 text-xs w-[88px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-[260px]">
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)} className="text-xs">{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Calendar
            mode="single"
            selected={fromISO(value) || undefined}
            month={calMonth}
            onMonthChange={setCalMonth}
            onSelect={onCalendarSelect}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
